import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  LinearProgress,
  Alert,
  CssBaseline,
  Chip,
  IconButton,
  Tooltip,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DownloadIcon from "@mui/icons-material/Download";
import CreditsBanner from "../Components/CreditsNudge";
import axios from "axios";
import {
  ContentBox,
  StyledButton,
  TitleText,
  SubtitleText,
  PageContainer,
} from "./Default.js";
import { message } from "../Constants/message.js";
import YouTubeTagExtractorContent from "../Contents/YouTubeTagExtractorContent.js";
import CommentSection from "../Components/CommentSection.js";

function YouTubeTagExtractor({ isLoggedIn, login, user }) {
  const [videoUrl, setVideoUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [resultData, setResultData] = useState(null);
  const [credits, setCredits] = useState(0);

  useEffect(() => {
    const primaryInputText = localStorage.getItem("primaryInputText");
    if (primaryInputText) {
      setVideoUrl(primaryInputText);
      localStorage.removeItem("primaryInputText");
    }
  }, []);
  const validateUrl = (url) => {
    const youtubeRegex = /^(https?:\/\/)?(www\.)?(youtube\.com|youtu\.?be)\/.+/;
    const isValid =
      youtubeRegex.test(url) &&
      (url.includes("v=") ||
        url.includes("youtu.be/") ||
        url.includes("/shorts/"));
    if (!isValid) {
      setError(
        "Please enter a valid YouTube video URL. It should be like https://www.youtube.com/watch?v=VIDEO_ID"
      );
      return false;
    }
    return true;
  };

  const handleExtractTags = async () => {
    if (!isLoggedIn) {
      setError(message.loginPrompt);
      setTimeout(() => {
        login();
      }, 1500);
      return;
    }

    setSuccess("");
    setError("");
    setResultData(null);

    if (!videoUrl) {
      setError("Please enter a YouTube video URL");
      return;
    }
    if (credits.remaining <= 0) {
      setError("You don’t have enough credits to extract YouTube video tags.");
      return;
    }

    if (!validateUrl(videoUrl)) return;

    setLoading(true);
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/youtube-tag-extractor`,
        { ytVideoUrl: videoUrl },
        {
          withCredentials: true,
          headers: { "Content-Type": "application/json" },
        }
      );

      const data = response.data;

      if (data.errorMessage) {
        setError(data.errorMessage);
        return;
      }

      setResultData(data);
      setSuccess(data.successMessage || "Tags extracted successfully!");
      const creditsResponse = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/credit`
      );
      setCredits({
        remaining: creditsResponse.data.remaining_credits || 0,
        availed: creditsResponse.data.availed_credits || 0,
        total: creditsResponse.data.total_credits || 0,
        loading: false,
      });
    } catch (err) {
      console.log(err);
      setError(
        err.response?.data?.errorMessage ||
          "Unable to extract tags. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = () => {
    const tagsText = resultData.tags.join(", ");
    navigator.clipboard.writeText(tagsText);
  };

  const handleDownload = () => {
    const element = document.createElement("a");
    const file = new Blob([resultData.tags.join(", ")], { type: "text/plain" });
    element.href = URL.createObjectURL(file);
    element.download = "youtube-tags.txt";
    document.body.appendChild(element);
    element.click();
  };

  return (
    <>
      {isLoggedIn && (
        <CreditsBanner credits={credits} setCredits={setCredits} />
      )}
      <CssBaseline />
      <PageContainer sx={{ paddingTop: 3 }}>
        <ContentBox>
          <TitleText variant="h3" align="center">
            YouTube Tag Extractor
          </TitleText>

          <SubtitleText variant="subtitle1" align="center">
            Extract SEO tags from any YouTube video
          </SubtitleText>

          <Box mb={3}>
            <TextField
              fullWidth
              label="YouTube Video URL"
              variant="outlined"
              value={videoUrl}
              onChange={(e) => {
                setVideoUrl(e.target.value);
                setError("");
                setSuccess("");
                setResultData(null);
              }}
              placeholder="https://www.youtube.com/watch?v=VIDEO_ID"
              size="medium"
              id="youtube-video-url"
              name="primary-textbox"
              sx={{ mb: 2 }}
              error={!!error}
              helperText={error}
            />
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              ✅ <b>1 YouTube video tag extract = 1 Credit</b>
            </Typography>
          </Box>

          {loading && <LinearProgress sx={{ mb: 2 }} />}

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {success}
            </Alert>
          )}

          <Box display="flex" justifyContent="center" gap={2}>
            <StyledButton
              variant="contained"
              color="primary"
              onClick={handleExtractTags}
              disabled={loading}
            >
              Extract Tags
            </StyledButton>
          </Box>

          {resultData && (
            <Box
              sx={{
                mt: 4,
                p: 3,
                borderRadius: 3,
                backgroundColor: "#f9f9fb",
                boxShadow: "0 2px 10px rgba(0,0,0,0.08)",
                transition: "all 0.3s ease-in-out",
                "&:hover": {
                  boxShadow: "0 4px 16px rgba(0,0,0,0.12)",
                },
              }}
            >
              {/* Icons container - aligned right */}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: 1,
                  mb: 2,
                }}
              >
                <Tooltip title="Copy Tags">
                  <IconButton
                    onClick={handleCopy}
                    sx={{
                      backgroundColor: "#fff",
                      "&:hover": { backgroundColor: "#f0f0f0" },
                    }}
                    size="small"
                  >
                    <ContentCopyIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Download Tags">
                  <IconButton
                    onClick={handleDownload}
                    sx={{
                      backgroundColor: "#fff",
                      "&:hover": { backgroundColor: "#f0f0f0" },
                    }}
                    size="small"
                  >
                    <DownloadIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>

              <Typography
                variant="h4"
                sx={{ mb: 2, fontWeight: 600 }}
                align="center"
              >
                Tags Extracted
              </Typography>

              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 1.2,
                  maxHeight: "250px",
                  overflowY: "auto",
                  justifyContent: "center",
                  pr: 1,
                }}
              >
                {resultData.tags.map((tag, i) => (
                  <Chip
                    key={i}
                    label={tag}
                    variant="filled"
                    sx={{
                      backgroundColor: "#d0f2d8",
                      color: "#1b5e20",
                      fontWeight: 600,
                      fontSize: "1rem",
                      borderRadius: "16px",
                      padding: "8px 16px",
                    }}
                  />
                ))}
              </Box>
            </Box>
          )}
        </ContentBox>
        <CommentSection
          toolId="youtube-tag-extractor"
          user={user}
          login={login}
          isLoggedIn={isLoggedIn}
        />
        <YouTubeTagExtractorContent />
      </PageContainer>
    </>
  );
}

export default YouTubeTagExtractor;
